import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';

import { throwError } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';

import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { AuthFlowTraceService } from '@ph-core/services/auth/auth-flow-trace.service';
import { ConfigService } from '@ph-core/services/config/config.service';
import { LoggerService } from '@ph-core/services/logger/logger.service';
import { PH_OVERLAY_DATA, PhOverlayRef } from '@ph-core/services/overlay';
import { Pattern, SameValue } from '@ph-core/validations/custom.validation';
import { RequiredValidator } from '@ph-core/validations/generic.validation';
import { UpdatePasswordRequest } from '@ph-model/request/update-password.request';
import { untilDestroyed } from '@ph-shared/utils/until-destroyed';
import { ConfigStore, ConfigStoreType } from '@ph-store/config/config.store';

import { FormInputControlComponent } from '../form-input-control/form-input-control.component';
import { IconComponent } from '../icon/icon-component';
import { PhButtonComponent } from '../ph-button/ph-button.component';

@Component({
  templateUrl: './update-password-popup.component.html',
  styleUrls: ['./update-password-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormInputControlComponent, PhButtonComponent, TranslateModule, IconComponent],
})
export class UpdatePasswordPopupComponent implements OnInit {
  data = inject<{
    username: string;
    stateToken?: string;
    flowType: 'OKTA' | 'LEGACY';
    actionType: 'UPDATE' | 'RESET';
  }>(PH_OVERLAY_DATA);

  requestForm!: FormGroup;
  emailSuccess: WritableSignal<boolean> = signal(false);

  private ref = inject<PhOverlayRef<UpdatePasswordPopupComponent>>(PhOverlayRef);
  private httpClient = inject(HttpClient);
  private fb = inject(FormBuilder);
  private configService = inject(ConfigService);
  private environment = this.configService.environment;
  private _isSSOFlow: boolean = this.environment.auth.sso;
  private _configStore: ConfigStoreType = inject(ConfigStore);
  private logger = inject(LoggerService);
  private authFlowTrace = inject(AuthFlowTraceService);

  readonly #untilDestroyed = untilDestroyed();

  ngOnInit(): void {
    this.logger.log(LoggerContext.Auth, 'UpdatePasswordPopup: opened', {
      username: this.data?.username,
      flowType: this.data?.flowType,
      actionType: this.data?.actionType,
      hasStateToken: !!this.data?.stateToken,
      stateTokenLength: this.data?.stateToken?.length ?? 0,
      isSSOFlow: this._isSSOFlow,
    });
    this.authFlowTrace.record('password-reset', 'update-password-popup:opened', {
      username: this.data?.username,
      flowType: this.data?.flowType,
      actionType: this.data?.actionType,
      hasStateToken: !!this.data?.stateToken,
    });

    this.requestForm = this.fb.group({
      currentPassword: this.fb.control(null, this.data?.flowType === 'LEGACY' ? [RequiredValidator] : []),
      newPassword: this.fb.control(null, [
        RequiredValidator,
        Pattern(
          this.data?.flowType === 'OKTA'
            ? /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[0-9])(?=.*[a-z]).{12,}$/
            : /^(?=.*[A-Z])(?=.*[!@#$%^&*_=+-])(?=.*[0-9])(?=.*[a-z]).{8,}$/,
          'errors.passwordComplexity'
        ),
      ]),
      confirmPassword: this.fb.control(null, [RequiredValidator, SameValue('newPassword', 'errors.samePassword')]),
    });
  }

  updatePassword(): void {
    if (this.requestForm.invalid) {
      this.logger.log(LoggerContext.Auth, 'UpdatePasswordPopup: submit blocked — form invalid', {
        flowType: this.data?.flowType,
        actionType: this.data?.actionType,
      });
      return;
    }

    if (this._isSSOFlow && this.data?.flowType === 'OKTA' && this.data?.actionType === 'RESET') {
      const payload = {
        username: this.data?.username,
        stateToken: this.data?.stateToken,
        password: this.requestForm.get('newPassword')?.value,
      };

      this.logger.log(LoggerContext.Auth, 'UpdatePasswordPopup: submitting Okta resetpassword', {
        username: payload.username,
        hasStateToken: !!payload.stateToken,
        stateTokenLength: payload.stateToken?.length ?? 0,
      });
      this.authFlowTrace.record('password-reset', 'update-password-popup:resetpassword-start', {
        username: payload.username,
        hasStateToken: !!payload.stateToken,
      });

      this.httpClient
        .post<void>(`${this.environment.apiUrl}/okta/resetpassword`, payload)
        .pipe(
          catchError((error) => {
            this.logger.warn(LoggerContext.Auth, 'UpdatePasswordPopup: resetpassword failed', { error });
            this.authFlowTrace.record('password-reset', 'update-password-popup:resetpassword-failed', {
              username: payload.username,
              error: String(error),
            });
            return throwError(() => error);
          }),
          tap(() => {
            this.emailSuccess.set(true);
            this.logger.log(LoggerContext.Auth, 'UpdatePasswordPopup: resetpassword success');
            this.authFlowTrace.record('password-reset', 'update-password-popup:resetpassword-success', {
              username: payload.username,
            });
          }),
          this.#untilDestroyed()
        )
        .subscribe();
    } else {
      const request: UpdatePasswordRequest = {
        application: this.environment.application,
        programId: this._configStore.clientConfig().programId ?? '',
        username: this.data.username,
        password: this.requestForm.get('currentPassword')?.value,
        newPassword: this.requestForm.get('newPassword')?.value,
        confirmNewPassword: this.requestForm.get('confirmPassword')?.value,
        noCaptcha: true,
      };
      this.logger.log(LoggerContext.Auth, 'UpdatePasswordPopup: submitting legacy updatepassword', {
        username: request.username,
        flowType: this.data?.flowType,
        actionType: this.data?.actionType,
      });
      this.httpClient
        .post<{ status: boolean }>(`${this.environment.apiUrl}/updatepassword`, request)
        .pipe(
          filter((data) => data && data.status),
          tap(() => {
            this.emailSuccess.set(true);
            this.requestForm.reset();
            this.logger.log(LoggerContext.Auth, 'UpdatePasswordPopup: legacy updatepassword success');
          }),
          catchError((err: Error) => {
            this.requestForm.reset();
            this.logger.warn(LoggerContext.Auth, 'UpdatePasswordPopup: legacy updatepassword failed', { err });

            return throwError(() => err);
          }),
          this.#untilDestroyed()
        )
        .subscribe();
    }
  }

  close(): void {
    this.logger.log(LoggerContext.Auth, 'UpdatePasswordPopup: closed', {
      emailSuccess: this.emailSuccess(),
      actionType: this.data?.actionType,
    });
    this.ref.close();
  }
}
